import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { classNames } from "../Components"

interface InputProps {
  className?: string;
  type?: "text" | "number" | "email" | "password";
  defaultValue?: string | number;
  maxLength?: number;
  name?: string;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  validInputs?: (string | number)[];
}

interface AutocompleteInputProps extends InputProps {
  id?: string;
  suggestions?: string[];
}

export function AutocompleteInput({ className, type, defaultValue, name, placeholder, readOnly, disabled, onChange, required, suggestions, id }: AutocompleteInputProps) {
  const [selected, setSelected] = useState<boolean>(false);
  const [value, setValue] = useState<string>(defaultValue ? defaultValue.toString() : "");
  const inputRef = useRef<HTMLInputElement>(null);

  // `AutocompleteInput` is a controlled component (its displayed value comes from
  // this `value` state, not the DOM), so `EditGroup`'s save-rejection handler
  // (issue #529) calling `formRef.current?.reset()` -- which only auto-reverts plain
  // uncontrolled `<input defaultValue>` elements -- has no effect on it by default.
  // Listen for the enclosing form's native `reset` event directly and resync back to
  // `defaultValue` ourselves.
  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form) {
      return;
    }
    const onReset = () => setValue(defaultValue ? defaultValue.toString() : "");
    form.addEventListener("reset", onReset);
    return () => form.removeEventListener("reset", onReset);
  }, [defaultValue]);

  // `defaultValue` only seeds `value` on mount -- without this, an externally
  // updated value (e.g. a realtime WS broadcast patching the underlying model
  // while this field is mounted, see `Tests/E2E/companion-connect-sync.spec.ts`)
  // is silently dropped. Skip the resync while the field is focused/selected so
  // an in-progress edit isn't clobbered out from under the user.
  useEffect(() => {
    if (selected) {
      return;
    }
    setValue(defaultValue ? defaultValue.toString() : "");
  }, [defaultValue]);

  const onChangeHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue(value);
    if (onChange) {
      onChange(e);
    }
  }, [setValue, onChange]);
  const onBlur = useCallback(() => {
    setSelected(false);
  }, [setSelected]);
  const onFocus = useCallback(() => {
    setSelected(true);
  }, [setSelected]);
  const input = (
    <input
      className={className}
      id={id}
      type={type}
      value={value}
      name={name}
      placeholder={placeholder}
      onBlur={onBlur}
      onChange={onChangeHandler}
      onFocus={onFocus}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      ref={inputRef}
    />
  );
  if (readOnly) {
    return input;
  }
  return (
    <span className={classNames(["AutocompleteInput", selected && "selected"])}>
      {suggestions && suggestions.length > 0 && (
        <div className="AutocompleteInput-options-container">
          <div className="AutocompleteInput-options">
            {suggestions.map((suggestion, index) => {
              if (suggestion == value) {
                return;
              }
              return (
                <div key={`suggestion-${index}`} className="AutocompleteInput-option" onMouseDown={() => {
                  setValue(suggestion);
                  setSelected(false);
                  if (onChange) {
                    onChange({ target: { value: suggestion } } as ChangeEvent<HTMLInputElement>);
                  }
                }}
                >
                  {suggestion}
                </div>
              );
            }).filter(Boolean)}
          </div>
        </div>
      )}
      {input}
    </span>
  );
}

interface NumberInputProps extends InputProps {
  format?: "int" | "float";
  sign?: boolean;
  style?: React.CSSProperties;
  // The unit the value is entered in, shown INSIDE the field as a suffix on the
  // displayed value (`5.0 m`) whenever the field is not being typed in -- the way
  // the depth thresholds have always presented themselves, restored after #1809
  // briefly moved it to a sibling label that wrapped onto its own line in
  // multi-column rows. Typing shows the bare number, so what is edited is never
  // the unit; on blur the suffix comes back. Unset renders the plain input alone.
  unit?: string;
  // Associates the field with an enclosing `BlockField label` via `labelFor`, so
  // the label names the input for assistive technology (and for `getByLabelText`).
  id?: string;
}

export const NumberInputData = {
  sanitize(raw: string, format: "int" | "float" | undefined, decimalPoint: string): string {
    let v = raw.replace(/[^0-9.,]/g, "");
    if (format == "int") {
      v = v.replaceAll(".", "").replaceAll(",", "");
    } else if (decimalPoint == ",") {
      v = v.replace(".", "");
      v = v.replaceAll(/(,.*),/g, "$1");
    } else {
      v = v.replace(",", "");
      v = v.replaceAll(/(\..*)\./g, "$1");
    }
    return v;
  },
  signedValue(sanitized: string, format: "int" | "float" | undefined, sign?: boolean): number {
    if (sanitized == "") return 0;
    const magnitude = format == "int" ? parseInt(sanitized) : parseFloat(sanitized.replace(",", "."));
    return magnitude * (sign == false ? -1 : 1);
  },
  isValid(signedValue: number, validInputs?: (string | number)[]): boolean {
    return !validInputs || validInputs.indexOf(signedValue) >= 0;
  },
};

export function NumberInput({ format, className, type, defaultValue, maxLength, name, placeholder, readOnly, disabled, onChange, sign, validInputs, style, unit, id }: NumberInputProps) {
  const decimalPoint = useMemo(() => (1.1).toLocaleString(navigator.language).substr(1, 1), []);
  const formattedDefaultValue = useMemo(() => decimalPoint == "," && format == "float" && defaultValue && defaultValue.toString().replaceAll(".", ","), [decimalPoint, defaultValue]);
  const [value, setValue] = useState<string>(formattedDefaultValue || (defaultValue != undefined ? defaultValue.toString() : ""));
  const [focused, setFocused] = useState<boolean>(false);

  // Same rationale as `AutocompleteInput`'s resync effect above: `defaultValue`
  // only seeds `value` on mount, so an externally updated value is otherwise
  // silently dropped while this field stays mounted. Skip while focused so an
  // in-progress edit isn't clobbered.
  useEffect(() => {
    if (focused) {
      return;
    }
    setValue(formattedDefaultValue || (defaultValue != undefined ? defaultValue.toString() : ""));
  }, [defaultValue, formattedDefaultValue]);

  const onChangeHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const originalValue = value;
    const newValue = NumberInputData.sanitize(e.target.value, format, decimalPoint);
    const signedValue = NumberInputData.signedValue(newValue, format, sign);
    if (!NumberInputData.isValid(signedValue, validInputs)) {
      setValue(originalValue);
      return;
    }
    setValue(newValue);
    if (onChange) {
      onChange(e);
    }
  }, [decimalPoint, format, setValue, value]);
  const onFocus = useCallback(() => setFocused(true), [setFocused]);
  const onBlur = useCallback(() => setFocused(false), [setFocused]);

  // The unit rides along on the field's own value rather than on an extra element,
  // so a unit field occupies exactly one grid cell and cannot wrap away from the
  // number it belongs to. Form submission is unaffected: `EditGroup.convertInput`
  // parses with `parseInt`/`parseFloat`, which stop at the suffix.
  // A degree sign sits tight against the number (`300°`); every other unit is a
  // word-like abbreviation and takes a space (`30 m`).
  const displayValue = unit && !focused && value != "" ? `${value}${unit == "°" ? "" : " "}${unit}` : value;

  const input = (
    <input
      className={className}
      id={id}
      type={type}
      inputMode={format == "int" ? "numeric" : format == "float" ? "decimal" : undefined}
      value={displayValue}
      maxLength={maxLength}
      name={name}
      placeholder={placeholder}
      onChange={onChangeHandler}
      onFocus={onFocus}
      onBlur={onBlur}
      style={style}
      disabled={disabled}
      readOnly={readOnly}
    />
  );
  return input;
}
